import { useMemo } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  ArrowLeft,
  ArrowRight,
  Check,
  ChevronRight,
  AlertTriangle,
  Sparkles,
  ExternalLink,
  BarChart3,
  Factory,
  Layers,
} from 'lucide-react'
import { solutions } from '@/data/solutions'
import { functions } from '@/data/functions'
import { caseStudies } from '@/data/caseStudies'
import { documentTypes } from '@/data/documentTypes'
import { useSEO } from '@/hooks/useSEO'
import { MarketingHeader } from '@/components/marketing/MarketingHeader'
import { MarketingFooter } from '@/components/marketing/MarketingFooter'

/* ──────────────────────────────────────────────────────────
   /solutions — hub page presenting BOTH axes (industry + function)
   ────────────────────────────────────────────────────────── */

export function SolutionsList() {
  useSEO({
    title: 'Solutions — by industry and function',
    description:
      'See how DocumentIQ solves document workflows across industries (manufacturing, logistics, professional services, financial services) and functions (AP automation, contract intelligence, customs & trade compliance, quality, claims processing).',
    canonical: 'https://documentiq.algoscale.com/solutions',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: 'DocumentIQ Solutions',
        url: 'https://documentiq.algoscale.com/solutions',
        mainEntity: {
          '@type': 'ItemList',
          itemListElement: [
            ...solutions.map((s, i) => ({
              '@type': 'ListItem',
              position: i + 1,
              url: `https://documentiq.algoscale.com/solutions/industry/${s.slug}`,
              name: s.industry,
            })),
            ...functions.map((f, i) => ({
              '@type': 'ListItem',
              position: solutions.length + i + 1,
              url: `https://documentiq.algoscale.com/solutions/function/${f.slug}`,
              name: f.name,
            })),
          ],
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://documentiq.algoscale.com' },
          { '@type': 'ListItem', position: 2, name: 'Solutions', item: 'https://documentiq.algoscale.com/solutions' },
        ],
      },
    ],
  })

  return (
    <div className="min-h-screen bg-white text-gray-900 antialiased">
      <MarketingHeader />

      <section className="py-16 sm:py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-gray-900 leading-tight">
            Solutions
          </h1>
          <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
            DocumentIQ adapts to the document workflow you are automating. Browse by industry
            for context tailored to your sector, or by function for the cross-industry workflow
            you are trying to run.
          </p>
        </div>
      </section>

      {/* By Function */}
      <section className="py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2.5 mb-8">
            <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center">
              <Layers size={18} className="text-blue-600" />
            </div>
            <h2 className="text-2xl font-semibold text-gray-900">By Function</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {functions.map((fn) => (
              <Link
                key={fn.slug}
                to={`/solutions/function/${fn.slug}`}
                className="group flex flex-col rounded-2xl border border-gray-200 bg-white hover:border-blue-200 hover:shadow-lg hover:shadow-blue-50 transition-all duration-300 p-6 sm:p-8"
              >
                <span className="inline-flex self-start px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-medium mb-3">
                  Function
                </span>
                <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors leading-snug">
                  {fn.name}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed mb-6 flex-1">
                  {fn.description}
                </p>
                <div className="flex items-center gap-6 pt-4 border-t border-gray-100">
                  {fn.stats.slice(0, 2).map((stat) => (
                    <div key={stat.label}>
                      <div className="text-lg font-bold text-blue-600">{stat.value}</div>
                      <div className="text-xs text-gray-500">{stat.label}</div>
                    </div>
                  ))}
                  <span className="ml-auto inline-flex items-center gap-1 text-sm font-medium text-blue-600 group-hover:gap-2 transition-all">
                    Learn more
                    <ChevronRight size={16} />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* By Industry */}
      <section className="py-12 sm:py-16 bg-gray-50 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2.5 mb-8">
            <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center">
              <Factory size={18} className="text-blue-600" />
            </div>
            <h2 className="text-2xl font-semibold text-gray-900">By Industry</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {solutions.map((sol) => (
              <Link
                key={sol.slug}
                to={`/solutions/industry/${sol.slug}`}
                className="group flex flex-col rounded-2xl border border-gray-200 bg-white hover:border-blue-200 hover:shadow-lg hover:shadow-blue-50 transition-all duration-300 p-6 sm:p-8"
              >
                <span className="inline-flex self-start px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-medium mb-3">
                  {sol.industry}
                </span>
                <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors leading-snug">
                  {sol.headline}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed mb-6 flex-1">
                  {sol.description}
                </p>
                <div className="flex items-center gap-6 pt-4 border-t border-gray-100">
                  {sol.stats.slice(0, 2).map((stat) => (
                    <div key={stat.label}>
                      <div className="text-lg font-bold text-blue-600">{stat.value}</div>
                      <div className="text-xs text-gray-500">{stat.label}</div>
                    </div>
                  ))}
                  <span className="ml-auto inline-flex items-center gap-1 text-sm font-medium text-blue-600 group-hover:gap-2 transition-all">
                    Learn more
                    <ChevronRight size={16} />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  )
}

/* ──────────────────────────────────────────────────────────
   /solutions/industry/:slug — industry detail
   ────────────────────────────────────────────────────────── */

export function IndustryDetail() {
  const { slug } = useParams<{ slug: string }>()
  const sol = solutions.find((s) => s.slug === slug)

  const jsonLd = useMemo(() => {
    if (!sol) return undefined
    return [
      {
        '@context': 'https://schema.org',
        '@type': 'Service',
        name: `DocumentIQ for ${sol.industry}`,
        description: sol.description,
        serviceType: 'AI Document Extraction',
        provider: {
          '@type': 'Organization',
          name: 'Algoscale',
          url: 'https://algoscale.com',
        },
        areaServed: 'Worldwide',
        audience: {
          '@type': 'BusinessAudience',
          name: sol.industry,
        },
        offers: {
          '@type': 'Offer',
          url: `https://documentiq.algoscale.com/solutions/industry/${sol.slug}`,
          availability: 'https://schema.org/InStock',
        },
        hasOfferCatalog: {
          '@type': 'OfferCatalog',
          name: `${sol.industry} Features`,
          itemListElement: sol.features.map((f, i) => ({
            '@type': 'Offer',
            position: i + 1,
            itemOffered: { '@type': 'Service', name: f.title, description: f.description },
          })),
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://documentiq.algoscale.com' },
          { '@type': 'ListItem', position: 2, name: 'Solutions', item: 'https://documentiq.algoscale.com/solutions' },
          { '@type': 'ListItem', position: 3, name: sol.industry, item: `https://documentiq.algoscale.com/solutions/industry/${sol.slug}` },
        ],
      },
      // ImageObject for the workflow diagram — Google Image Search indexes
      // SVGs better with explicit width/height/caption metadata than from
      // the inline <title>/<desc> alone.
      {
        '@context': 'https://schema.org',
        '@type': 'ImageObject',
        contentUrl: `https://documentiq.algoscale.com/diagrams/${sol.slug}-document-workflow.svg`,
        url: `https://documentiq.algoscale.com/diagrams/${sol.slug}-document-workflow.svg`,
        name: `${sol.industry} document workflow with DocumentIQ`,
        caption: `Source documents → DocumentIQ AI extraction → destination systems for ${sol.industry.toLowerCase()}.`,
        description: `Diagram showing a typical ${sol.industry.toLowerCase()} document workflow automated end-to-end with DocumentIQ.`,
        encodingFormat: 'image/svg+xml',
        width: { '@type': 'QuantitativeValue', value: 1200, unitCode: 'E37' },
        height: { '@type': 'QuantitativeValue', value: 720, unitCode: 'E37' },
        representativeOfPage: true,
        creditText: 'DocumentIQ',
        creator: { '@id': 'https://documentiq.algoscale.com/#organization' },
        license: `https://documentiq.algoscale.com/solutions/industry/${sol.slug}`,
        acquireLicensePage: `https://documentiq.algoscale.com/solutions/industry/${sol.slug}`,
      },
    ]
  }, [sol])

  useSEO({
    title: sol ? (sol.seoTitle ?? `${sol.industry} — Document Extraction`) : 'Solution',
    description: sol ? (sol.seoDescription ?? sol.description) : 'DocumentIQ industry solution',
    canonical: sol ? `https://documentiq.algoscale.com/solutions/industry/${sol.slug}` : undefined,
    ogImage: sol ? `/og/industry/${sol.slug}.png` : undefined,
    jsonLd,
  })

  if (!sol) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500 mb-4">Solution not found.</p>
          <Link to="/solutions" className="text-blue-600 font-medium hover:underline">View all solutions</Link>
        </div>
      </div>
    )
  }

  const relatedCaseStudies = caseStudies.filter((cs) => sol.caseStudySlugs.includes(cs.slug))
  const relatedFunctions = functions.filter((f) => sol.functionSlugs.includes(f.slug))

  return (
    <div className="min-h-screen bg-white text-gray-900 antialiased">
      <MarketingHeader />

      <div className="bg-gray-50 border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Link to="/solutions" className="hover:text-gray-900 transition-colors flex items-center gap-1"><ArrowLeft size={14} /> Solutions</Link>
            <span>/</span>
            <span className="text-gray-700">Industry</span>
            <span>/</span>
            <span className="text-gray-900 font-medium">{sol.industry}</span>
          </div>
        </div>
      </div>

      {/* Hero */}
      <section className="py-12 sm:py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="inline-flex px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-medium mb-4">{sol.industry}</span>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 leading-tight mb-6">{sol.headline}</h1>
          <p className="text-gray-600 leading-relaxed max-w-3xl">{sol.heroDescription}</p>
        </div>
      </section>

      {/* Stats */}
      <section className="border-y border-gray-200 bg-blue-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-3 gap-8 text-center">
            {sol.stats.map((stat) => (
              <div key={stat.label}>
                <div className="text-3xl sm:text-4xl font-bold text-blue-600">{stat.value}</div>
                <div className="text-sm text-gray-600 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Workflow diagram — vertical-specific SVG with full SEO alt + caption */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-3">How a {sol.industry.toLowerCase()} document workflow runs on DocumentIQ</h2>
          <p className="text-gray-600 leading-relaxed max-w-3xl mb-8">
            The same documents your {sol.industry.toLowerCase()} teams handle today — flowing into DocumentIQ
            for AI extraction, then back into the systems of record where the data belongs.
          </p>
          <figure className="rounded-2xl border border-gray-200 bg-gray-50 p-3 sm:p-6 overflow-hidden">
            <img
              src={`/diagrams/${sol.slug}-document-workflow.svg`}
              alt={`Diagram of a ${sol.industry.toLowerCase()} document workflow automated with DocumentIQ — source documents on the left flow into DocumentIQ for AI extraction, then into destination systems on the right.`}
              loading="lazy"
              width={1200}
              height={720}
              className="w-full h-auto block"
            />
            <figcaption className="mt-3 text-xs text-gray-500 text-center">
              {sol.industry} document workflow: source documents &rarr; DocumentIQ AI extraction &rarr; downstream systems.
            </figcaption>
          </figure>
        </div>
      </section>

      {/* Pain Points */}
      <section className="py-12 sm:py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2.5 mb-8">
            <div className="w-9 h-9 rounded-lg bg-red-50 flex items-center justify-center"><AlertTriangle size={18} className="text-red-500" /></div>
            <h2 className="text-2xl font-semibold text-gray-900">The Challenges</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {sol.painPoints.map((pp) => (
              <div key={pp.title} className="p-6 rounded-xl border border-gray-200">
                <h3 className="font-semibold text-gray-900 mb-2">{pp.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{pp.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2.5 mb-8">
            <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center"><Sparkles size={18} className="text-blue-600" /></div>
            <h2 className="text-2xl font-semibold text-gray-900">How DocumentIQ Solves This</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {sol.features.map((feat) => (
              <div key={feat.title} className="p-6 rounded-xl border border-gray-200 bg-gray-50">
                <h3 className="font-semibold text-gray-900 mb-2 flex items-start gap-2">
                  <Check size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
                  {feat.title}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed pl-6">{feat.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Functions we cover in this industry — cross-link into function pages */}
      {relatedFunctions.length > 0 && (
        <section className="py-12 sm:py-16">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-2.5 mb-8">
              <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center"><Layers size={18} className="text-blue-600" /></div>
              <h2 className="text-2xl font-semibold text-gray-900">Functions we cover in {sol.industry}</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {relatedFunctions.map((fn) => (
                <Link
                  key={fn.slug}
                  to={`/solutions/function/${fn.slug}`}
                  className="group p-6 rounded-xl border border-gray-200 hover:border-blue-200 hover:shadow-md transition-all"
                >
                  <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">{fn.name}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{fn.description}</p>
                  <span className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 mt-3 group-hover:gap-2 transition-all">
                    Learn more <ChevronRight size={14} />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Case Studies */}
      {relatedCaseStudies.length > 0 && (
        <section className="py-12 sm:py-16 bg-gray-50">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-2.5 mb-8">
              <div className="w-9 h-9 rounded-lg bg-green-50 flex items-center justify-center"><BarChart3 size={18} className="text-green-600" /></div>
              <h2 className="text-2xl font-semibold text-gray-900">Case Studies</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedCaseStudies.map((cs) => (
                <Link
                  key={cs.slug}
                  to={`/case-studies/${cs.slug}`}
                  className="group p-6 rounded-xl border border-gray-200 bg-white hover:border-blue-200 hover:shadow-md transition-all"
                >
                  <div className="text-2xl font-bold text-blue-600 mb-1">{cs.keyMetric.value}</div>
                  <div className="text-xs text-gray-500 mb-3">{cs.keyMetric.label}</div>
                  <h3 className="text-sm font-medium text-gray-900 group-hover:text-blue-600 transition-colors leading-snug">{cs.headline}</h3>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Related from Algoscale */}
      <section className="py-12 bg-gray-50 border-t border-gray-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-sm font-semibold text-gray-500 mb-4">Related services from Algoscale</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {sol.relatedLinks.map((link) => (
              <a
                key={link.url}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="p-4 rounded-lg border border-gray-200 bg-white hover:border-blue-200 hover:shadow-sm transition-all group"
              >
                <div className="flex items-center gap-1.5 mb-1">
                  <span className="text-sm font-medium text-gray-900 group-hover:text-blue-600 transition-colors">{link.title}</span>
                  <ExternalLink size={12} className="text-gray-400" />
                </div>
                <p className="text-xs text-gray-500 leading-relaxed">{link.description}</p>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-blue-700">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">Ready to automate your {sol.industry.toLowerCase()} document workflows?</h2>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/register" className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-white text-blue-600 font-medium hover:bg-blue-50 transition-colors shadow-md text-sm">
              Start for Free <ArrowRight size={18} />
            </Link>
            <a href="mailto:askus@algoscale.com" className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-blue-300 text-white font-medium hover:bg-blue-500/20 transition-colors text-sm">
              Talk to Sales
            </a>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  )
}

/* ──────────────────────────────────────────────────────────
   /solutions/function/:slug — function detail
   ────────────────────────────────────────────────────────── */

export function FunctionDetail() {
  const { slug } = useParams<{ slug: string }>()
  const fn = functions.find((f) => f.slug === slug)

  const jsonLd = useMemo(() => {
    if (!fn) return undefined
    return [
      {
        '@context': 'https://schema.org',
        '@type': 'Service',
        name: `DocumentIQ — ${fn.name}`,
        description: fn.description,
        serviceType: fn.name,
        provider: {
          '@type': 'Organization',
          name: 'Algoscale',
          url: 'https://algoscale.com',
        },
        areaServed: 'Worldwide',
        offers: {
          '@type': 'Offer',
          url: `https://documentiq.algoscale.com/solutions/function/${fn.slug}`,
          availability: 'https://schema.org/InStock',
        },
        hasOfferCatalog: {
          '@type': 'OfferCatalog',
          name: `${fn.name} Features`,
          itemListElement: fn.features.map((f, i) => ({
            '@type': 'Offer',
            position: i + 1,
            itemOffered: { '@type': 'Service', name: f.title, description: f.description },
          })),
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://documentiq.algoscale.com' },
          { '@type': 'ListItem', position: 2, name: 'Solutions', item: 'https://documentiq.algoscale.com/solutions' },
          { '@type': 'ListItem', position: 3, name: fn.name, item: `https://documentiq.algoscale.com/solutions/function/${fn.slug}` },
        ],
      },
      // ImageObject for the workflow diagram on this function page
      {
        '@context': 'https://schema.org',
        '@type': 'ImageObject',
        contentUrl: `https://documentiq.algoscale.com/diagrams/${fn.slug}-document-workflow.svg`,
        url: `https://documentiq.algoscale.com/diagrams/${fn.slug}-document-workflow.svg`,
        name: `${fn.name} document workflow with DocumentIQ`,
        caption: `Source documents → DocumentIQ AI extraction → destination systems for ${fn.name.toLowerCase()}.`,
        description: `Diagram showing how ${fn.name.toLowerCase()} runs on DocumentIQ end-to-end.`,
        encodingFormat: 'image/svg+xml',
        width: { '@type': 'QuantitativeValue', value: 1200, unitCode: 'E37' },
        height: { '@type': 'QuantitativeValue', value: 720, unitCode: 'E37' },
        representativeOfPage: true,
        creditText: 'DocumentIQ',
        creator: { '@id': 'https://documentiq.algoscale.com/#organization' },
        license: `https://documentiq.algoscale.com/solutions/function/${fn.slug}`,
        acquireLicensePage: `https://documentiq.algoscale.com/solutions/function/${fn.slug}`,
      },
    ]
  }, [fn])

  useSEO({
    title: fn ? (fn.seoTitle ?? `${fn.name} — AI Document Extraction`) : 'Function',
    description: fn ? (fn.seoDescription ?? fn.description) : 'DocumentIQ function solution',
    canonical: fn ? `https://documentiq.algoscale.com/solutions/function/${fn.slug}` : undefined,
    ogImage: fn ? `/og/function/${fn.slug}.png` : undefined,
    jsonLd,
  })

  if (!fn) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500 mb-4">Function not found.</p>
          <Link to="/solutions" className="text-blue-600 font-medium hover:underline">View all solutions</Link>
        </div>
      </div>
    )
  }

  const relatedCaseStudies = caseStudies.filter((cs) => fn.caseStudySlugs.includes(cs.slug))
  const relatedIndustries = solutions.filter((s) => fn.industrySlugs.includes(s.slug))
  const relatedDocs = documentTypes.filter((d) => fn.documentTypeSlugs.includes(d.slug))

  return (
    <div className="min-h-screen bg-white text-gray-900 antialiased">
      <MarketingHeader />

      <div className="bg-gray-50 border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Link to="/solutions" className="hover:text-gray-900 transition-colors flex items-center gap-1"><ArrowLeft size={14} /> Solutions</Link>
            <span>/</span>
            <span className="text-gray-700">Function</span>
            <span>/</span>
            <span className="text-gray-900 font-medium">{fn.name}</span>
          </div>
        </div>
      </div>

      {/* Hero */}
      <section className="py-12 sm:py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="inline-flex px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-medium mb-4">Function</span>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 leading-tight mb-6">{fn.headline}</h1>
          <p className="text-gray-600 leading-relaxed max-w-3xl">{fn.heroDescription}</p>
        </div>
      </section>

      {/* Stats */}
      <section className="border-y border-gray-200 bg-blue-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-3 gap-8 text-center">
            {fn.stats.map((stat) => (
              <div key={stat.label}>
                <div className="text-3xl sm:text-4xl font-bold text-blue-600">{stat.value}</div>
                <div className="text-sm text-gray-600 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Workflow diagram — function-specific SVG with full SEO alt + caption */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-3">How {fn.name.toLowerCase()} runs on DocumentIQ</h2>
          <p className="text-gray-600 leading-relaxed max-w-3xl mb-8">
            The end-to-end document path for {fn.name.toLowerCase()} — source files from your existing systems,
            AI extraction in DocumentIQ, structured data pushed to the platforms your team already uses.
          </p>
          <figure className="rounded-2xl border border-gray-200 bg-gray-50 p-3 sm:p-6 overflow-hidden">
            <img
              src={`/diagrams/${fn.slug}-document-workflow.svg`}
              alt={`Diagram of the ${fn.name.toLowerCase()} document workflow automated with DocumentIQ — source documents on the left flow into DocumentIQ for AI extraction, then into destination systems on the right.`}
              loading="lazy"
              width={1200}
              height={720}
              className="w-full h-auto block"
            />
            <figcaption className="mt-3 text-xs text-gray-500 text-center">
              {fn.name} workflow: source documents &rarr; DocumentIQ AI extraction &rarr; downstream systems.
            </figcaption>
          </figure>
        </div>
      </section>

      {/* Pain Points */}
      <section className="py-12 sm:py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2.5 mb-8">
            <div className="w-9 h-9 rounded-lg bg-red-50 flex items-center justify-center"><AlertTriangle size={18} className="text-red-500" /></div>
            <h2 className="text-2xl font-semibold text-gray-900">The Challenges</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {fn.painPoints.map((pp) => (
              <div key={pp.title} className="p-6 rounded-xl border border-gray-200 bg-white">
                <h3 className="font-semibold text-gray-900 mb-2">{pp.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{pp.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2.5 mb-8">
            <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center"><Sparkles size={18} className="text-blue-600" /></div>
            <h2 className="text-2xl font-semibold text-gray-900">How DocumentIQ Solves This</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {fn.features.map((feat) => (
              <div key={feat.title} className="p-6 rounded-xl border border-gray-200 bg-white">
                <h3 className="font-semibold text-gray-900 mb-2 flex items-start gap-2">
                  <Check size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
                  {feat.title}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed pl-6">{feat.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Used in these industries — cross-link */}
      {relatedIndustries.length > 0 && (
        <section className="py-12 sm:py-16">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-2.5 mb-8">
              <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center"><Factory size={18} className="text-blue-600" /></div>
              <h2 className="text-2xl font-semibold text-gray-900">Used in these industries</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {relatedIndustries.map((sol) => (
                <Link
                  key={sol.slug}
                  to={`/solutions/industry/${sol.slug}`}
                  className="group p-6 rounded-xl border border-gray-200 hover:border-blue-200 hover:shadow-md transition-all"
                >
                  <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">{sol.industry}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{sol.description}</p>
                  <span className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 mt-3 group-hover:gap-2 transition-all">
                    Learn more <ChevronRight size={14} />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Related document types */}
      {relatedDocs.length > 0 && (
        <section className="py-12 bg-gray-50 border-t border-gray-200">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h3 className="text-sm font-semibold text-gray-500 mb-4">Document types we extract for this function</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {relatedDocs.map((d) => (
                <Link
                  key={d.slug}
                  to={`/extract/${d.slug}`}
                  className="p-4 rounded-lg border border-gray-200 bg-white hover:border-blue-200 hover:shadow-sm transition-all group"
                >
                  <span className="text-sm font-medium text-gray-900 group-hover:text-blue-600 transition-colors">{d.name}</span>
                  <p className="text-xs text-gray-500 leading-relaxed mt-1">{d.description}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Case Studies */}
      {relatedCaseStudies.length > 0 && (
        <section className="py-12 sm:py-16">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-2.5 mb-8">
              <div className="w-9 h-9 rounded-lg bg-green-50 flex items-center justify-center"><BarChart3 size={18} className="text-green-600" /></div>
              <h2 className="text-2xl font-semibold text-gray-900">Case Studies</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedCaseStudies.map((cs) => (
                <Link
                  key={cs.slug}
                  to={`/case-studies/${cs.slug}`}
                  className="group p-6 rounded-xl border border-gray-200 hover:border-blue-200 hover:shadow-md transition-all"
                >
                  <div className="text-2xl font-bold text-blue-600 mb-1">{cs.keyMetric.value}</div>
                  <div className="text-xs text-gray-500 mb-3">{cs.keyMetric.label}</div>
                  <h3 className="text-sm font-medium text-gray-900 group-hover:text-blue-600 transition-colors leading-snug">{cs.headline}</h3>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Related from Algoscale */}
      <section className="py-12 bg-gray-50 border-t border-gray-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-sm font-semibold text-gray-500 mb-4">Related services from Algoscale</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {fn.relatedLinks.map((link) => (
              <a
                key={link.url}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="p-4 rounded-lg border border-gray-200 bg-white hover:border-blue-200 hover:shadow-sm transition-all group"
              >
                <div className="flex items-center gap-1.5 mb-1">
                  <span className="text-sm font-medium text-gray-900 group-hover:text-blue-600 transition-colors">{link.title}</span>
                  <ExternalLink size={12} className="text-gray-400" />
                </div>
                <p className="text-xs text-gray-500 leading-relaxed">{link.description}</p>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-blue-700">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">Ready to automate {fn.name.toLowerCase()}?</h2>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/register" className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-white text-blue-600 font-medium hover:bg-blue-50 transition-colors shadow-md text-sm">
              Start for Free <ArrowRight size={18} />
            </Link>
            <a href="mailto:askus@algoscale.com" className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-blue-300 text-white font-medium hover:bg-blue-500/20 transition-colors text-sm">
              Talk to Sales
            </a>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  )
}
